import { getAdminBuildInfo } from "@/utils/buildInfo";
import {
  resolveAdminDeployStage,
  resolveAdminTelemetryEnvironment,
} from "@/utils/deployEnvironment";
import {
  enqueueFrontendTelemetryRecord,
  TELEMETRY_SOURCE,
} from "@/utils/logging/frontendTelemetrySink";

const ADMIN_SESSION_BOOT_STORAGE_KEY = "HC_ADMIN_SESSION_BOOT_EMITTED";
const ADMIN_SESSION_BOOT_MEMORY_KEY = "__HC_ADMIN_SESSION_BOOT_EMITTED__";

const readEnvValue = (key: string): string | undefined =>
  (import.meta.env as Record<string, string | undefined>)[key];

const resolveDeployStage = (): string =>
  resolveAdminDeployStage({
    env: {
      VITE_HC_DEPLOY_ENV: readEnvValue("VITE_HC_DEPLOY_ENV"),
      MODE: import.meta.env.MODE,
    },
  });

const getInMemorySessionBootFallback = (): boolean => {
  const fallbackWindow = window as Window & {
    [ADMIN_SESSION_BOOT_MEMORY_KEY]?: boolean;
  };

  if (fallbackWindow[ADMIN_SESSION_BOOT_MEMORY_KEY]) {
    return false;
  }

  fallbackWindow[ADMIN_SESSION_BOOT_MEMORY_KEY] = true;
  return true;
};

const markSessionBootEmitted = (): boolean => {
  if (typeof window === "undefined") {
    return false;
  }

  try {
    if (window.sessionStorage.getItem(ADMIN_SESSION_BOOT_STORAGE_KEY) === "1") {
      return false;
    }

    window.sessionStorage.setItem(ADMIN_SESSION_BOOT_STORAGE_KEY, "1");
    return true;
  } catch {
    // Fall back to page-lifetime memory so privacy-restricted browsers still emit once
    // without turning this production bypass marker into route-change spam.
    return getInMemorySessionBootFallback();
  }
};

export interface AdminSessionBootSignalArgs {
  authState: "authenticated" | "anonymous";
  route: string;
}

/**
 * Emits the single approved production-threshold bypass for the admin frontend.
 *
 * CRITICAL:
 * - Use this extremely sparingly. This helper exists only for the once-per-session
 *   `ADMIN_SESSION_BOOT` operational marker used to verify active traffic after cutovers.
 * - Do not generalize this into a new "always" log level or reuse it for routine UX events.
 * - Keep attributes low-cardinality and free of secrets, emails, org names, or raw payloads.
 */
export const emitAdminSessionBootSignal = ({
  authState,
  route,
}: AdminSessionBootSignalArgs): void => {
  if (import.meta.env.MODE === "test" || typeof window === "undefined") {
    return;
  }

  if (!markSessionBootEmitted()) {
    return;
  }

  const buildInfo = getAdminBuildInfo();

  enqueueFrontendTelemetryRecord({
    timestamp: new Date().toISOString(),
    level: "info",
    severityText: "INFO",
    message: "Admin session boot",
    source: TELEMETRY_SOURCE,
    attributes: {
      component: "App",
      operation: "ADMIN_SESSION_BOOT",
      service: TELEMETRY_SOURCE,
      environment: resolveAdminTelemetryEnvironment(),
      deployStage: resolveDeployStage(),
      sourceRepo: "HelloCashflowMonorepo",
      runtimeSource: "monorepo",
      origin: window.location.origin,
      hostname: window.location.hostname,
      route,
      authState,
      buildVersion: buildInfo.buildVersion,
      ...(buildInfo.buildSha ? { buildSha: buildInfo.buildSha } : {}),
    },
  });
};
